import { defineMessages } from 'react-intl';

/**
 * The copy of the welcome page (design: onboarding a1-a8, copy table in
 * docs/superpowers/design-reference/music24-onboarding/README.md). Product copy, not ASD-STE100.
 * `<grad>` marks the part of a headline that gets the brand gradient.
 */
export const welcomeMessages = defineMessages({
  eyebrow: { id: 'app.sml.welcome.eyebrow', defaultMessage: 'Welcome to MUSIC24' },
  headlineDefault: {
    id: 'app.sml.welcome.headline.default',
    defaultMessage: 'One song becomes <grad>a whole campaign.</grad>',
  },
  leadDefault: {
    id: 'app.sml.welcome.lead.default',
    defaultMessage:
      'Add a song. MUSIC24 makes the posts and the videos, plans your release and publishes it on every channel.',
  },
  headlinePromote: { id: 'app.sml.welcome.headline.promote', defaultMessage: 'Be everywhere on release day.' },
  leadPromote: {
    id: 'app.sml.welcome.lead.promote',
    defaultMessage:
      'Add a song. MUSIC24 plans every post from the first teaser to six weeks after, and publishes each one on time on every channel.',
  },
  headlineAnalytics: { id: 'app.sml.welcome.headline.analytics', defaultMessage: 'Know what brings you listeners.' },
  leadAnalytics: {
    id: 'app.sml.welcome.lead.analytics',
    defaultMessage: 'See your streams next to every post you make, and do more of what works on the next release.',
  },
  headlinePublicData: {
    id: 'app.sml.welcome.headline.publicData',
    defaultMessage: 'Find where your music can grow.',
  },
  leadPublicData: {
    id: 'app.sml.welcome.lead.publicData',
    defaultMessage:
      'See the playlists, countries and artists that move songs like yours. Then aim your next release there.',
  },
  headlineTeams: {
    id: 'app.sml.welcome.headline.teams',
    defaultMessage: 'One song becomes <grad>a whole campaign.</grad>',
  },
  teamsNote: {
    id: 'app.sml.welcome.teamsNote',
    defaultMessage: 'Start with your first artist. Every paid plan adds more artists and your team when you need them.',
  },
  trialAlreadyUsed: {
    id: 'app.sml.welcome.trial.alreadyUsed',
    defaultMessage: 'This email already had its free week, so you start on Free. Everything you make stays.',
  },
  trialSeePlans: { id: 'app.sml.welcome.trial.seePlans', defaultMessage: 'See the plans' },
  nameLabel: { id: 'app.sml.welcome.name.label', defaultMessage: 'Your artist name' },
  nameHint: { id: 'app.sml.welcome.name.hint', defaultMessage: 'As your fans know you' },
  namePlaceholder: { id: 'app.sml.welcome.name.placeholder', defaultMessage: 'Your artist name' },
  nameRequired: {
    id: 'app.sml.welcome.name.required',
    defaultMessage: 'Type your artist name to go on. It goes on every post, video and link.',
  },
  // Plain words for a first-time user (QA 2026-10-28, lit-21): no "SmartLink", no "pre-save".
  addressHint: {
    id: 'app.sml.welcome.address.hintSongPages',
    defaultMessage: 'Your song pages will have this web address.',
  },
  addressLabel: { id: 'app.sml.welcome.address.label', defaultMessage: 'Your address' },
  addressEdit: { id: 'app.sml.welcome.address.edit', defaultMessage: 'Edit address' },
  // "Free" read as a price (lit-21): the chip says the address is not taken.
  addressAvailable: { id: 'app.sml.welcome.address.available', defaultMessage: 'Available' },
  addressChecking: { id: 'app.sml.welcome.address.checking', defaultMessage: 'Checking' },
  addressTaken: { id: 'app.sml.welcome.address.taken', defaultMessage: 'Taken' },
  addressTakenLine: { id: 'app.sml.welcome.address.takenLine', defaultMessage: 'Another artist has this address.' },
  addressUseSuggestion: { id: 'app.sml.welcome.address.useSuggestion', defaultMessage: 'Use {slug}' },
  addressIllegal: { id: 'app.sml.welcome.address.illegal', defaultMessage: 'Not allowed' },
  addressIllegalLine: {
    id: 'app.sml.welcome.address.illegalLine.length',
    defaultMessage: 'The address must have 2 to 60 letters or numbers. A dash cannot start or end it.',
  },
  addressReserved: { id: 'app.sml.welcome.address.reserved', defaultMessage: 'Reserved' },
  addressReservedLine: {
    id: 'app.sml.welcome.address.reservedLine',
    defaultMessage: 'This address is kept for MUSIC24. Pick another one.',
  },
  errorNetwork: {
    id: 'app.sml.welcome.error.network',
    defaultMessage: 'We could not save your name. Check the connection and try again.',
  },
  example: { id: 'app.sml.welcome.example', defaultMessage: 'Continue to demo' },
  addSong: { id: 'app.sml.welcome.addSong', defaultMessage: 'Set up my own song' },
  exampleNote: {
    id: 'app.sml.welcome.example.note',
    defaultMessage:
      "The example is LaziiBee's single Forests: a finished campaign, from the first post to the numbers.",
  },
  signOut: { id: 'app.sml.welcome.signOut', defaultMessage: 'Sign out' },
  loopAlt: {
    id: 'app.sml.welcome.loop.alt',
    defaultMessage: 'Forests by LaziiBee: four feed posts of its Neon release kit',
  },
  loopLabel: { id: 'app.sml.welcome.loop.label', defaultMessage: 'LaziiBee · example' },
  loopPhaseSong: { id: 'app.sml.welcome.loop.phase.song', defaultMessage: 'Song' },
  loopPhaseKit: { id: 'app.sml.welcome.loop.phase.kit', defaultMessage: 'Kit' },
  loopPhasePlan: { id: 'app.sml.welcome.loop.phase.plan', defaultMessage: 'Plan' },
  loopPhaseNumbers: { id: 'app.sml.welcome.loop.phase.numbers', defaultMessage: 'Numbers' },
  memberEyebrow: { id: 'app.sml.welcome.member.eyebrow', defaultMessage: 'You are on the team' },
  memberTitle: { id: 'app.sml.welcome.member.title', defaultMessage: 'Welcome to {workspace}.' },
  memberTitleNoName: { id: 'app.sml.welcome.member.titleNoName', defaultMessage: 'Welcome to the team.' },
  memberLead: {
    id: 'app.sml.welcome.member.lead',
    defaultMessage:
      '{inviter} added you to the team. See how one song becomes a whole campaign, then open the workspace and get to work.',
  },
  memberLeadNoInviter: {
    id: 'app.sml.welcome.member.leadNoInviter',
    defaultMessage: 'See how one song becomes a whole campaign, then open the workspace and get to work.',
  },
  memberOpen: { id: 'app.sml.welcome.member.open', defaultMessage: 'Open the workspace' },
});
